/*
  <product-rail> — High-performance product rail for Best Sellers, Gifts, Caps, and Kids rails.
  
  Mobile-first architecture:
  - Mobile & Touch devices: Uses pure, native GPU-accelerated CSS touch-scrolling with
    smooth momentum, scroll snapping, and zero main-thread CPU usage.
  - Desktop (Hover & Pointer): Smooth hardware-accelerated auto-advance with cached dimensions
    to prevent layout reflows (zero getBoundingClientRect in hot paths).
*/
if (!customElements.get('product-rail')) {
  customElements.define(
    'product-rail',
    class ProductRail extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-rail-viewport]');
        this.track = this.querySelector('[data-rail-track]');
        if (!this.viewport || !this.track) return;

        this.speed = Math.abs(parseFloat(this.dataset.speed)) || 35;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.mobileMedia = window.matchMedia('(max-width: 749px)');

        this.rtl = getComputedStyle(this).direction === 'rtl';

        this.pos = 0;
        this.lastApplied = 0;
        this.lastFrame = 0;
        this.rafId = null;
        this.held = 0;
        this.visible = false;
        this.loopPrimed = false;
        this.direction = 1;
        this.cachedGap = 12;
        this.cachedCardWidth = 200;

        this.tick = this.tick.bind(this);
        this.refresh = this.refresh.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);

        this.bindArrows();
        this.bindHolds();
        this.observe();

        this.reduceMotion.addEventListener('change', this.onMotionPreferenceChange);
        this.mobileMedia.addEventListener('change', this.refresh);
        window.addEventListener('resize', this.refresh, { passive: true });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.refresh);
          this.resizeObserver.observe(this.track);
        }

        this.measure();
        this.refresh();
      }

      disconnectedCallback() {
        this.stop();
        clearTimeout(this.settleTimer);
        this.intersectionObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.reduceMotion?.removeEventListener('change', this.onMotionPreferenceChange);
        this.mobileMedia?.removeEventListener('change', this.refresh);
        window.removeEventListener('resize', this.refresh);
      }

      /* ---------- state & measurement ---------- */

      get isMobile() {
        return this.mobileMedia.matches || ('ontouchstart' in window && navigator.maxTouchPoints > 0 && window.innerWidth < 750);
      }

      get maxScroll() {
        return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      }

      get overflows() {
        return this.maxScroll > 4;
      }

      get canLoop() {
        return !this.isMobile && this.viewport.scrollWidth > this.viewport.clientWidth * 1.5;
      }

      get shouldAutoScroll() {
        return (
          this.dataset.motion === 'auto' &&
          !this.rtl &&
          !this.isMobile &&
          !this.reduceMotion.matches &&
          this.overflows
        );
      }

      measure() {
        const first = this.track.firstElementChild;
        if (first) {
          this.cachedCardWidth = first.offsetWidth || 200;
        }
        this.cachedGap = parseFloat(getComputedStyle(this.track).columnGap) || 12;
      }

      refresh() {
        this.measure();
        this.toggleAttribute('data-overflows', this.overflows);
        this.updateArrows();

        if (this.shouldAutoScroll) {
          if (this.canLoop && !this.loopPrimed) this.primeLoop();
          if (this.visible && !this.held) this.start();
        } else {
          this.stop();
        }
      }

      onMotionPreferenceChange() {
        if (this.reduceMotion.matches) this.stop();
        this.refresh();
      }

      /* ---------- looping ---------- */

      primeLoop() {
        if (this.isMobile) return;
        const last = this.track.lastElementChild;
        if (!last || last === this.track.firstElementChild) return;
        const width = (last.offsetWidth || this.cachedCardWidth) + this.cachedGap;
        this.track.insertBefore(last, this.track.firstElementChild);
        this.viewport.scrollLeft = width;
        this.sync();
        this.loopPrimed = true;
      }

      wrapOnce() {
        if (this.isMobile) return false;
        const first = this.track.firstElementChild;
        const last = this.track.lastElementChild;
        if (!first || !last || first === last) return false;

        const firstWidth = (first.offsetWidth || this.cachedCardWidth) + this.cachedGap;
        if (this.viewport.scrollLeft >= firstWidth) {
          this.track.appendChild(first);
          this.viewport.scrollLeft -= firstWidth;
          this.sync();
          return true;
        }

        if (this.viewport.scrollLeft <= 1) {
          const lastWidth = (last.offsetWidth || this.cachedCardWidth) + this.cachedGap;
          this.track.insertBefore(last, first);
          this.viewport.scrollLeft += lastWidth;
          this.sync();
          return true;
        }

        return false;
      }

      wrap() {
        if (this.isMobile || !this.canLoop || !this.loopPrimed) return;
        for (let step = 0; step < 8; step += 1) {
          if (!this.wrapOnce()) return;
        }
      }

      sync() {
        this.pos = this.viewport.scrollLeft;
        this.lastApplied = this.pos;
      }

      /* ---------- the loop ---------- */

      start() {
        if (this.isMobile || this.rafId !== null) return;
        this.sync();
        this.lastFrame = 0;
        this.rafId = requestAnimationFrame(this.tick);
        this.setAttribute('data-running', '');
      }

      stop() {
        if (this.rafId !== null) cancelAnimationFrame(this.rafId);
        this.rafId = null;
        this.removeAttribute('data-running');
      }

      tick(timestamp) {
        if (!this.shouldAutoScroll) {
          this.stop();
          return;
        }

        if (!this.lastFrame) this.lastFrame = timestamp;
        const elapsed = (timestamp - this.lastFrame) / 1000;
        this.lastFrame = timestamp;

        if (elapsed > 0 && elapsed < 0.25) {
          if (Math.abs(this.viewport.scrollLeft - this.lastApplied) > 1.5) {
            this.pos = this.viewport.scrollLeft;
          }

          this.pos += this.speed * this.direction * elapsed;

          if (this.canLoop) {
            this.direction = 1;
          } else {
            const max = this.maxScroll;
            if (this.pos >= max) {
              this.pos = max;
              this.direction = -1;
            } else if (this.pos <= 0) {
              this.pos = 0;
              this.direction = 1;
            }
          }

          this.viewport.scrollLeft = this.pos;
          this.lastApplied = this.viewport.scrollLeft;
          this.wrap();
        }

        this.rafId = requestAnimationFrame(this.tick);
      }

      /* ---------- interaction ---------- */

      hold() {
        this.held += 1;
        this.stop();
      }

      release() {
        this.held = Math.max(0, this.held - 1);
        if (!this.held && this.visible && this.shouldAutoScroll) this.start();
      }

      bindHolds() {
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.hold();
        });
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.release();
        });

        this.addEventListener('focusin', () => this.hold());
        this.addEventListener('focusout', () => this.release());

        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      }

      onScroll() {
        this.updateArrows();
        if (this.isMobile || this.rafId !== null) return;
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          this.wrap();
          this.updateArrows();
        }, 140);
      }

      observe() {
        if (!('IntersectionObserver' in window)) {
          this.visible = true;
          return;
        }
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.visible = entries[0].isIntersecting;
            if (this.visible && !this.held && this.shouldAutoScroll) this.start();
            else if (!this.visible) this.stop();
          },
          { rootMargin: '100px' }
        );
        this.intersectionObserver.observe(this);
      }

      bindArrows() {
        this.prev = this.querySelector('[data-rail-prev]');
        this.next = this.querySelector('[data-rail-next]');
        this.prev?.addEventListener('click', () => this.nudge(-1));
        this.next?.addEventListener('click', () => this.nudge(1));
      }

      nudge(direction) {
        this.hold();
        const step = this.isMobile 
          ? (this.cachedCardWidth + this.cachedGap) 
          : (this.cachedCardWidth + this.cachedGap) * 2;
        this.viewport.scrollBy({ left: step * direction, behavior: 'smooth' });
        setTimeout(() => this.release(), 450);
      }

      updateArrows() {
        if (!this.prev || !this.next) return;
        if (!this.overflows) {
          this.prev.hidden = true;
          this.next.hidden = true;
          return;
        }
        this.prev.hidden = false;
        this.next.hidden = false;
        if (this.canLoop) {
          this.prev.disabled = false;
          this.next.disabled = false;
        } else {
          this.prev.disabled = this.viewport.scrollLeft <= 2;
          this.next.disabled = this.viewport.scrollLeft >= this.maxScroll - 2;
        }
      }
    }
  );
}

// Shop Rail Tabs Custom Element
if (!customElements.get('shop-rail-tabs')) {
  customElements.define(
    'shop-rail-tabs',
    class ShopRailTabs extends HTMLElement {
      connectedCallback() {
        this.pills = Array.from(this.querySelectorAll('[role="tab"], [data-rail-tab], .shop-rail__pill, .kids-shop__chip'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"], [data-rail-panel], .shop-rail__panel, .kids-shop__panel-pane'));
        this.viewAll = this.querySelector('[data-rail-viewall]');

        this.pills.forEach((pill) => {
          pill.addEventListener('click', (e) => {
            e.preventDefault();
            this.activate(pill);
          });
        });
      }

      activate(targetPill) {
        const targetId = targetPill.getAttribute('data-rail-tab') ||
                         targetPill.getAttribute('aria-controls') ||
                         targetPill.id;

        const targetIndex = this.pills.indexOf(targetPill);

        this.pills.forEach((pill) => {
          const isTarget = pill === targetPill;
          pill.setAttribute('aria-selected', isTarget ? 'true' : 'false');
          pill.tabIndex = isTarget ? 0 : -1;
        });

        this.panels.forEach((panel, index) => {
          const panelId = panel.getAttribute('data-rail-panel') || panel.id;
          const isTarget = (targetId && (panelId === targetId || panel.id === targetId)) ||
                           (index === targetIndex);
          panel.hidden = !isTarget;
          if (isTarget) {
            const rail = panel.querySelector('product-rail');
            rail?.refresh();
          }
        });

        const collectionUrl = targetPill.dataset.collectionUrl || targetPill.getAttribute('data-collection-url');
        if (this.viewAll && collectionUrl) {
          this.viewAll.href = collectionUrl;
        }
      }
    }
  );
}
